import { useMemo } from 'react';

import { _zipCode, Company, ServiceTitanBusinessUnit, ServiceTitanZone } from '_lib/api/apiTypes.shim';

import { BusinessUnitItem, ZipCodeItem } from '../ZipCodeTable';

/**
 * Helper to get business unit display information
 */
const getBusinessUnitsItem = (
  businessUnitIds: string[] | null | undefined,
  businessUnits: ServiceTitanBusinessUnit[]
): BusinessUnitItem | null => {
  if (!businessUnitIds) {
    return null; // No BUs means N/A (RelateCX only zip item)
  }

  if (businessUnitIds.length === 0) {
    return { names: [], display: 'ALL' };
  }

  if (businessUnitIds.length === 1) {
    const bu = businessUnits.find((b) => b.providerId === businessUnitIds[0]);
    return {
      names: bu ? [bu.name] : [],
      display: bu ? bu.name : 'Inactive Business Unit'
    };
  }

  // Multiple business units
  const names = businessUnitIds.map((id) => {
    const bu = businessUnits.find((b) => b.providerId === id);
    return (bu && bu.name.trim()) || `Inactive Business Unit with ID: ${id}`;
  });

  return {
    names,
    display: names.join('\n'),
    isMultiple: true
  };
};

/**
 * Converts a zip code with zone info into a ZipCodeItem
 */
const toZipCodeItem = (
  zip: string,
  zone: ServiceTitanZone | null,
  businessUnits: ServiceTitanBusinessUnit[] | null,
  selected: boolean
): ZipCodeItem => {
  return {
    zip,
    zone,
    businessUnitsItem: businessUnits && zone ? getBusinessUnitsItem(zone.businessUnitIds, businessUnits) : null,
    selected
  };
};

/**
 * Processing result containing categorized zip code items
 */
export type ProcessedZipCodeData = {
  /** Valid zip codes from Service Titan */
  servicedZips: ZipCodeItem[];
  /** Valid ST zips not in company.callCenter.zipCodesServiced */
  unassignedStZips: ZipCodeItem[];
  /** Invalid format zips from Service Titan */
  invalidZips: ZipCodeItem[];
  /** Duplicate zips found in Service Titan zones */
  duplicateZips: ZipCodeItem[];
};

/**
 * Hook to process zones and business units into categorized zip code items
 *
 * Handles:
 * - Invalid zip code detection (non-US/CA format)
 * - Duplicate detection across zones
 * - Splitting zips into serviced vs unassigned
 * - Relate-only zips (in company but not in ST)
 */
export const useZipCodeProcessor = ({
  zones,
  businessUnits,
  company
}: {
  zones: ServiceTitanZone[] | undefined;
  businessUnits: ServiceTitanBusinessUnit[] | undefined;
  company: Company | undefined;
}): ProcessedZipCodeData => {
  return useMemo(() => {
    const emptyResult: ProcessedZipCodeData = {
      servicedZips: [],
      unassignedStZips: [],
      invalidZips: [],
      duplicateZips: []
    };

    if (!zones || !businessUnits || !company) {
      return emptyResult;
    }

    const zipCodesServiced = company.callCenter?.zipCodesServiced || [];
    const seenZips = new Set<string>();
    const invalidZips: ZipCodeItem[] = [];
    const duplicateZips: ZipCodeItem[] = [];
    const validZipToZone = new Map<string, ServiceTitanZone>();

    // Process all zips from zones
    zones.forEach((zone) => {
      zone.zips.forEach((zip) => {
        // Check for invalid format
        if (!_zipCode().safeParse(zip).success) {
          invalidZips.push(toZipCodeItem(zip, zone, businessUnits, false));
          return;
        }

        // Check for duplicates
        if (seenZips.has(zip)) {
          duplicateZips.push(toZipCodeItem(zip, zone, businessUnits, false));
          return;
        }

        seenZips.add(zip);
        validZipToZone.set(zip, zone);
      });
    });

    // Split into serviced and unassigned
    const servicedZips: ZipCodeItem[] = [];
    const unassignedStZips: ZipCodeItem[] = [];

    // Process ST zips
    validZipToZone.forEach((zone, zip) => {
      const isServiced = zipCodesServiced.includes(zip);
      const item = toZipCodeItem(zip, zone, businessUnits, isServiced);

      if (isServiced) {
        servicedZips.push(item);
      } else {
        unassignedStZips.push(item);
      }
    });

    // Process Relate-only zips (in company but not in ST)
    zipCodesServiced.forEach((zip) => {
      if (!validZipToZone.has(zip)) {
        // Relate-only zip
        servicedZips.push(toZipCodeItem(zip, null, null, true));
      }
    });

    // Sort all arrays by zip code
    const sortByZip = (a: ZipCodeItem, b: ZipCodeItem) => a.zip.localeCompare(b.zip);

    servicedZips.sort(sortByZip);
    unassignedStZips.sort(sortByZip);
    invalidZips.sort(sortByZip);
    duplicateZips.sort(sortByZip);

    return {
      servicedZips,
      unassignedStZips,
      invalidZips,
      duplicateZips
    };
  }, [zones, businessUnits, company]);
};
